/**
 * ConnectorDetailSection — a connector's deep detail as a DEDICATED, full-width
 * in-page section (a "place you navigate to"), not a floating overlay.
 *
 * Rationale (Jul 2026): the schema browser + live sample data are deep,
 * destination work. A side drawer is too narrow and a modal-over-modal (the
 * integrations hub is already a fullscreen modal) reads as transient. A
 * dedicated inline takeover — with a "← Connettori" back affordance and a
 * URL-addressable ``?detail=`` param wired by the route — is the SOTA / Claude
 * pattern: URL-addressable, back/forward works, no z-index stacking.
 *
 * Capability-driven (DRY): the tabs come from the manifest
 * (``getConnectorTabs``), so DB/ERP get Schema · Contesto · Esempi while a
 * spreadsheet gets Schema only. The header actions (Verifica · Scopri ·
 * Credenziali) are relational-database work and render ONLY for database/erp;
 * a spreadsheet — a materialised workbook with no host/credentials — shows the
 * schema with no credential chrome.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  BookOpenText,
  ChevronLeft,
  Database,
  ListChecks,
  Loader2,
  RefreshCw,
  Settings,
  Table2,
  TestTube,
} from "lucide-react";
import * as React from "react";

import type { ConnectionSummary } from "@ando/core";
import { Button } from "@ando/ui";

import {
  BusinessContextEditor,
  ChatExamplesManager,
  ConnectorDetailPanel,
  type ConnectorDrawerTab,
  ConnectorStatus,
  ImportDictionaryDialog,
  SchemaViewer,
} from "@/features/connectors/components";
import { useCollectValues } from "@/features/connectors/hooks/useCollectValues";
import { getConnectorTabs } from "@/features/connectors/manifest";

export interface ConnectorDetailSectionProps {
  /** The connection being managed (the route guards this is non-null). */
  readonly connection: ConnectionSummary;
  /** Tab to open initially. */
  readonly initialTabId?: string;
  /** Return to the connection list (clears the ``?detail`` search param). */
  readonly onBack: () => void;
  readonly onDiscover: (connId: string) => void;
  readonly onEditCredentials: (connection: ConnectionSummary) => void;
  readonly onTestConnection: (connection: ConnectionSummary) => void;
}

export const ConnectorDetailSection: React.FC<ConnectorDetailSectionProps> = ({
  connection,
  initialTabId,
  onBack,
  onDiscover,
  onEditCredentials,
  onTestConnection,
}) => {
  const { i18n } = useLingui();

  // "Valori": values-only sampling, the non-destructive sibling of "Scopri".
  // For a hand-curated schema (vendor data dictionary, ``user_edited``) this
  // is the ONLY refresh a person should run: no AI, descriptions and business
  // context untouched. The BE answers 202 and works in the background, so the
  // feedback is a short inline notice rather than a progress dialog.
  const collectValues = useCollectValues();
  const [valuesNotice, setValuesNotice] = React.useState<"started" | "error" | null>(null);
  React.useEffect(() => {
    if (valuesNotice !== "started") return;
    const t = window.setTimeout(() => setValuesNotice(null), 8000);
    return () => window.clearTimeout(t);
  }, [valuesNotice]);
  const handleCollectValues = React.useCallback(() => {
    setValuesNotice(null);
    collectValues.mutate(
      { connId: connection.conn_id },
      {
        onSuccess: () => setValuesNotice("started"),
        onError: () => setValuesNotice("error"),
      },
    );
  }, [collectValues, connection.conn_id]);

  // "Dizionario": upload the vendor data dictionary (Excel), preview the
  // coverage and start the automatic completion pipeline. The dialog owns
  // its own step state; the section only opens/closes it.
  const [dictionaryOpen, setDictionaryOpen] = React.useState(false);

  // Relational-database work (host/credentials, live discovery) — only these
  // capabilities get the verify/discover/edit header actions. A spreadsheet is
  // a materialised workbook: schema only, no credential chrome.
  const isRelationalDb = connection.capability === "database" || connection.capability === "erp";

  const tabs: readonly ConnectorDrawerTab[] = getConnectorTabs(connection.capability)
    .map((id): ConnectorDrawerTab | null => {
      switch (id) {
        case "schema":
          return {
            id,
            label: <Trans>Schema</Trans>,
            content: (
              <SchemaViewer
                connId={connection.conn_id}
                onStartDiscovery={() => onDiscover(connection.conn_id)}
              />
            ),
          };
        case "context":
          return {
            id,
            label: <Trans>Contesto</Trans>,
            content: <BusinessContextEditor connId={connection.conn_id} />,
          };
        case "examples":
          return {
            id,
            label: <Trans>Esempi</Trans>,
            content: <ChatExamplesManager connId={connection.conn_id} />,
          };
        default:
          return null;
      }
    })
    .filter((t): t is ConnectorDrawerTab => t !== null);

  return (
    <div className="flex h-[calc(100dvh-7.5rem)] min-h-0 flex-col gap-2">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-1 rounded-md py-1 pr-2 font-medium text-[12.5px] text-ink-3 transition-colors hover:text-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
      >
        <ChevronLeft size={15} strokeWidth={2} aria-hidden />
        <Trans>Connettori</Trans>
      </button>

      <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-hairline bg-cream">
        <ConnectorDetailPanel
          key={connection.conn_id}
          title={connection.connection_key ?? connection.conn_id}
          subtitle={connection.provider ?? connection.vendor}
          status={<ConnectorStatus connection={connection} variant="inline" />}
          icon={
            <span
              aria-hidden
              className="grid h-8 w-8 place-items-center rounded-lg border border-ando-orange/40 bg-ando-orange-soft text-ando-orange"
            >
              {isRelationalDb ? (
                <Database size={16} strokeWidth={1.75} />
              ) : (
                <Table2 size={16} strokeWidth={1.75} />
              )}
            </span>
          }
          tabs={tabs}
          initialTabId={initialTabId}
          headerActions={
            isRelationalDb ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onTestConnection(connection)}
                  aria-label={i18n._(msg`Verifica connessione`)}
                >
                  <TestTube size={14} strokeWidth={2} aria-hidden />
                  <Trans>Verifica</Trans>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDiscover(connection.conn_id)}
                  aria-label={i18n._(msg`Avvia scoperta schema`)}
                >
                  <RefreshCw size={14} strokeWidth={2} aria-hidden />
                  <Trans>Scopri</Trans>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCollectValues}
                  disabled={collectValues.isPending}
                  aria-label={i18n._(msg`Raccogli i valori dei codici senza rigenerare le descrizioni`)}
                  title={i18n._(
                    msg`Campiona i valori distinti delle colonne codice/flag. Nessuna AI: descrizioni e contesto restano intatti.`,
                  )}
                >
                  {collectValues.isPending ? (
                    <Loader2 size={14} strokeWidth={2} className="animate-spin" aria-hidden />
                  ) : (
                    <ListChecks size={14} strokeWidth={2} aria-hidden />
                  )}
                  <Trans>Valori</Trans>
                </Button>
                {valuesNotice ? (
                  <span
                    role="status"
                    aria-live="polite"
                    className={
                      valuesNotice === "error"
                        ? "self-center text-[11.5px] text-danger"
                        : "self-center text-[11.5px] text-ink-3"
                    }
                  >
                    {valuesNotice === "error" ? (
                      <Trans>Raccolta valori non avviata.</Trans>
                    ) : (
                      <Trans>Raccolta valori avviata: compaiono nello schema entro pochi minuti.</Trans>
                    )}
                  </span>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setDictionaryOpen(true)}
                  aria-label={i18n._(msg`Importa dizionario dati`)}
                  title={i18n._(
                    msg`Carica il dizionario dati del fornitore (Excel) e completa lo schema in automatico.`,
                  )}
                >
                  <BookOpenText size={14} strokeWidth={2} aria-hidden />
                  <Trans>Dizionario</Trans>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditCredentials(connection)}
                  aria-label={i18n._(msg`Modifica credenziali`)}
                >
                  <Settings size={14} strokeWidth={2} aria-hidden />
                  <Trans>Credenziali</Trans>
                </Button>
              </>
            ) : undefined
          }
        />
      </div>

      {isRelationalDb ? (
        <ImportDictionaryDialog
          open={dictionaryOpen}
          onOpenChange={setDictionaryOpen}
          connId={connection.conn_id}
        />
      ) : null}
    </div>
  );
};
ConnectorDetailSection.displayName = "ConnectorDetailSection";
